/**
 * `useMaterialLibrary` — live read of every material library item
 * in the org, with optional client-side filtering by category +
 * text search.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialLibrary/useMaterialLibrary.ts
 *
 * Path: `materialLibrary` where `orgId == X`. Sort: name asc.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type {
  MaterialCategory,
  MaterialLibraryItem,
} from '@/lib/materialLibrary';

export type UseMaterialLibraryResult = {
  data: MaterialLibraryItem[];
  loading: boolean;
};

export function useMaterialLibrary(
  orgId: string | null | undefined,
  /** Optional case-insensitive substring filter — matched against
   *  name, brand, variety, make, category. */
  searchText?: string,
  /** Optional category filter. Falsy = all categories. */
  category?: MaterialCategory | null,
): UseMaterialLibraryResult {
  const [all, setAll] = useState<MaterialLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setAll([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'materialLibrary'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: MaterialLibraryItem[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<MaterialLibraryItem, 'id'>),
        }));
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setAll(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useMaterialLibrary] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId]);

  const data = useMemo(() => {
    let rows = all;
    if (category) rows = rows.filter((r) => r.category === category);
    const q = (searchText ?? '').trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.name, r.brand, r.variety, r.make, r.category]
          .filter(Boolean)
          .some((s) => s.toLowerCase().includes(q)),
      );
    }
    return rows;
  }, [all, category, searchText]);

  return { data, loading };
}
